import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { checkABNumber } from "../../utilities/FormValidation";

/* -------------------------------------------------------------------------- */
/*  Shared building blocks                                                    */
/* -------------------------------------------------------------------------- */

/**
 * One dialog shell for all popups:
 *  - header with icon, title, optional subtitle and a close button
 *  - full screen on phones (unless fullScreenOnMobile is false)
 *  - footer buttons stack full width on phones, safe-area aware
 */
export const AppDialog = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  tone = "secondary",
  maxWidth = "sm",
  fullScreenOnMobile = true,
  actions,
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const accent = theme.palette[tone]?.main || theme.palette.secondary.main;
  const titleId = `dlg-${String(title).replace(/\W+/g, "-").toLowerCase()}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      fullScreen={isMobile && fullScreenOnMobile}
      aria-labelledby={titleId}
      PaperProps={{ sx: { borderRadius: { xs: fullScreenOnMobile ? 0 : 3, sm: 3 } } }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              borderRadius: 2,
              color: accent,
              bgcolor: alpha(accent, 0.15),
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            id={titleId}
            variant="subtitle1"
            noWrap
            sx={{ fontWeight: 700, lineHeight: 1.3 }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: "block" }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton aria-label="Close" onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: "20px !important", pb: 2 }}>
        {children}
      </DialogContent>

      {actions && (
        <DialogActions
          sx={{
            gap: 1,
            px: { xs: 2, sm: 3 },
            pt: 1.5,
            pb: "max(16px, env(safe-area-inset-bottom))",
            borderTop: `1px solid ${theme.palette.divider}`,
            flexDirection: { xs: "column-reverse", sm: "row" },
            "& > :not(:first-of-type)": { ml: 0 },
            "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

/**
 * Click, tap or drag-and-drop file picker.
 * Calls onChange with an event-like object ({ target: { files } }), so your
 * existing handlers (handleDocumentChange, handleUploadABFile ...) work as-is.
 * `accept` is also enforced on drop, which a plain <input accept> does not do.
 */
export const FileDropZone = ({ onChange, accept, hint }) => {
  const theme = useTheme();
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");

  const allowed = accept
    ? accept
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
    : null;
  const isAllowed = (file) =>
    !allowed || allowed.some((ext) => file.name.toLowerCase().endsWith(ext));

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!isAllowed(file)) {
      setError(`Only ${allowed.join(", ")} files are allowed.`);
      return;
    }
    setError("");
    onChange({ target: { files: e.dataTransfer.files } });
  };

  return (
    <Box>
      <Box
        component="label"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          p: { xs: 3, sm: 4 },
          textAlign: "center",
          cursor: "pointer",
          borderRadius: 2,
          border: `2px dashed ${
            dragging ? theme.palette.secondary.main : theme.palette.divider
          }`,
          bgcolor: dragging
            ? alpha(theme.palette.secondary.main, 0.08)
            : theme.palette.background.default,
          transition: "all .2s ease",
          "&:hover": { borderColor: theme.palette.secondary.main },
          "&:focus-within": {
            outline: `2px solid ${theme.palette.secondary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <Box
          component="input"
          type="file"
          accept={accept}
          sx={visuallyHidden}
          onClick={(e) => {
            e.target.value = null; // lets the same file be picked twice
          }}
          onChange={(e) => {
            setError("");
            onChange(e);
          }}
        />
        <CloudUploadOutlinedIcon
          sx={{ fontSize: 40, color: "secondary.main" }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Drag and drop a file here, or{" "}
          </Box>
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
            Tap to choose a file
          </Box>
        </Typography>
        <Button
          component="span"
          tabIndex={-1}
          variant="contained"
          color="secondary"
          size="small"
          sx={{ display: { xs: "none", sm: "inline-flex" } }}
        >
          Browse files
        </Button>
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
      </Box>
      {error && (
        <Typography
          role="alert"
          variant="caption"
          color="error"
          sx={{ display: "block", mt: 1 }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

/** The chosen file, with a remove button. */
export const SelectedFileCard = ({ name, caption, onRemove }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          borderRadius: 2,
          color: "secondary.main",
          bgcolor: alpha(theme.palette.secondary.main, 0.15),
        }}
      >
        <InsertDriveFileOutlinedIcon />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, wordBreak: "break-all" }}
        >
          {name}
        </Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Box>
      <IconButton aria-label="Remove file" onClick={onRemove}>
        <DeleteOutlineIcon />
      </IconButton>
    </Box>
  );
};



const OrderDetailDialogs = ({
  openAttachment,
  onCloseAttachment,
  attachmentSelectedRow,
  onDocumentChange, 
  onRemoveDocument,

  openDelete,
  onCloseDelete, 
  onConfirmDelete, 

  formOpen,
  onCloseForm, 
  ABNo,
  setABNo,
  onSaveABNo, 

  // Send AB file to customer
  ABSendOpen,
  onCloseABSend, 
  sendCustomerABFile,
  onUploadCustomerABFile, 
  onClearCustomerABFile, 
  onSendCustomerAB, 

  ABReceivedOpen,
  onCloseABReceived, 
  ABFiles,
  onUploadABFile,
  onClearABFile, 
  onUploadAB, 
}) => {
  const attachmentName = attachmentSelectedRow?.data?.attachmentName;
  const isDrw = attachmentSelectedRow?.data?.type === "DRW*";
  const abNoError = ABNo ? checkABNumber(ABNo) : "";
  const abNoInvalid = Boolean(checkABNumber(ABNo));

  return (
    <>
      {/* 1. Upload documents */}
      <AppDialog
        open={openAttachment}
        onClose={onCloseAttachment}
        title="Upload document"
        subtitle={attachmentSelectedRow?.data?.type}
        icon={<CloudUploadOutlinedIcon />}
        actions={
          <Button
            variant={attachmentName ? "contained" : "outlined"}
            color="secondary"
            onClick={onCloseAttachment}
          >
            {attachmentName ? "Done" : "Close"}
          </Button>
        }
      >
        {!attachmentName ? (
          <FileDropZone
            accept={isDrw ? ".drw, .prw" : undefined}
            hint={isDrw ? "Only .drw or .prw files" : undefined}
            onChange={onDocumentChange}
          />
        ) : (
          <SelectedFileCard
            name={attachmentName}
            caption="Attached to this row. Press Save Order to upload."
            onRemove={onRemoveDocument}
          />
        )}
      </AppDialog>

      {/* 2. Delete order */}
      <AppDialog
        open={openDelete}
        onClose={onCloseDelete}
        title="Delete order"
        icon={<WarningAmberRoundedIcon />}
        tone="error"
        maxWidth="xs"
        fullScreenOnMobile={false}
        actions={
          <>
            <Button variant="outlined" color="inherit" onClick={onCloseDelete} autoFocus>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={onConfirmDelete}
            >
              Delete
            </Button>
          </>
        }
      >
        <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
          Delete this order from the NOS system?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Deleted orders can be restored from the recycle bin.
        </Typography>
      </AppDialog>

      {/* 3. Add AB No */}
      <AppDialog
        open={formOpen}
        onClose={onCloseForm}
        title="Add AB number"
        subtitle="Required to mark the order as acknowledged"
        icon={<SaveOutlinedIcon />}
        actions={
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SaveOutlinedIcon />}
            onClick={onSaveABNo}
            disabled={abNoInvalid}
          >
            Save
          </Button>
        }
      >
        <TextField
          autoFocus
          fullWidth
          label="AB number"
          name="abNumber"
          value={ABNo}
          error={Boolean(abNoError)}
          helperText={abNoError || " "}
          onChange={(e) => setABNo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !abNoInvalid) onSaveABNo();
          }}
        />
      </AppDialog>

      {/* 4. Send AB file to customer */}
      <AppDialog
        open={ABSendOpen}
        onClose={onCloseABSend}
        title="Send AB file to customer"
        subtitle="PDF only"
        icon={<SendIcon />}
        actions={
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SendIcon />}
            onClick={onSendCustomerAB}
            disabled={!sendCustomerABFile?.name}
          >
            Send to customer
          </Button>
        }
      >
        {!sendCustomerABFile?.name ? (
          <FileDropZone
            accept=".pdf"
            hint="PDF files only"
            onChange={onUploadCustomerABFile}
          />
        ) : (
          <SelectedFileCard
            name={sendCustomerABFile.name}
            caption="Ready to send"
            onRemove={onClearCustomerABFile}
          />
        )}
      </AppDialog>

      {/* 5. Upload the AB files */}
      <AppDialog
        open={ABReceivedOpen}
        onClose={onCloseABReceived}
        title="Upload AB file"
        subtitle="PDF only"
        icon={<CloudUploadOutlinedIcon />}
        actions={
          <Button
            variant="contained"
            color="secondary"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={onUploadAB}
            disabled={!ABFiles?.attachmentName}
          >
            Upload AB file
          </Button>
        }
      >
        {!ABFiles?.attachmentName ? (
          <FileDropZone
            accept=".pdf"
            hint="PDF files only"
            onChange={onUploadABFile}
          />
        ) : (
          <SelectedFileCard
            name={ABFiles.attachmentName}
            caption="Ready to upload"
            onRemove={onClearABFile}
          />
        )}
      </AppDialog>
    </>
  );
};

export default OrderDetailDialogs;